/**
 * Which language the interface speaks.
 *
 * One module owns the choice so nothing else has to guess: the strings table
 * reads it to pick a dictionary, `Intl` reads it to format a date, `<html
 * lang>` carries it for screen readers and font selection, and the stylesheet
 * keys its per-script typography off that same attribute.
 *
 * The locale is chrome, never rules. No generated grid, stored result, share
 * payload or telemetry value depends on it — a Spanish player and an English
 * player race the same grid and file the same numbers (GDD §3.5).
 *
 * Adding a language is four registrations and nothing else:
 *   1. its tag in `Locale`, `LOCALES` and `LOCALE_NAME`, here;
 *   2. a dictionary in `ui/strings.ts`, and its entry in `DICTIONARIES`;
 *   3. the tag added to `locale`'s enum in `backend/events.schema.json`,
 *      because the usage stream may only carry what the registry declares,
 *      then `npm run privacy` to regenerate the page that discloses it;
 *   4. the tag in `index.html`'s JSON-LD `inLanguage`, which is a claim about
 *      this game made to search engines rather than a thing the app reads.
 *      `tests/unit/structured-data.test.ts` fails until it matches.
 *
 * And nothing else: the picker, the detector and the layout tests all read
 * `LOCALES`, so they pick the language up on their own.
 */

/**
 * A language the game speaks, named by its own BCP-47 tag.
 *
 * The tag *is* the identifier: it is what `<html lang>` carries, what `Intl`
 * formats against, and what storage and the usage stream file the choice
 * under. Two parallel identifier spaces — a short code and a tag it maps to —
 * would only be two things to keep in step, and a short code cannot express
 * `zh-Hant` at all, because there the script is the whole distinction.
 */
export type Locale =
  | 'id-ID'
  | 'da-DK'
  | 'de-DE'
  | 'en-US'
  | 'es-419'
  | 'es-ES'
  | 'fr-FR'
  | 'it-IT'
  | 'nb-NO'
  | 'nl-NL'
  | 'pl-PL'
  | 'pt-BR'
  | 'sv-SE'
  | 'vi-VN'
  | 'tr-TR'
  | 'hi-IN'
  | 'th-TH'
  | 'ja-JP'
  | 'zh-Hans'
  | 'zh-Hant'
  | 'ko-KR';

/** Every language the game speaks, in the order the picker lists them. */
export const LOCALES: readonly Locale[] = [
  'id-ID',
  'da-DK',
  'de-DE',
  'en-US',
  'es-419',
  'es-ES',
  'fr-FR',
  'it-IT',
  'nb-NO',
  'nl-NL',
  'pl-PL',
  'pt-BR',
  'sv-SE',
  'vi-VN',
  'tr-TR',
  'hi-IN',
  'th-TH',
  'ja-JP',
  'zh-Hans',
  'zh-Hant',
  'ko-KR',
];

/** What the game falls back to when it speaks nothing the browser asked for. */
export const FALLBACK_LOCALE: Locale = 'en-US';

/**
 * Each language's name in its own language, and never translated.
 *
 * This is what makes the picker usable by the player who needs it most: a
 * Spanish speaker who has somehow landed in an English interface cannot read
 * the word "Spanish", but can read "Español". An endonym is the one label
 * that works no matter which language the interface is currently speaking.
 */
export const LOCALE_NAME: Record<Locale, string> = {
  'id-ID': 'Bahasa Indonesia',
  'da-DK': 'Dansk',
  'de-DE': 'Deutsch',
  'en-US': 'English',
  'es-419': 'Español (Latinoamérica)',
  'es-ES': 'Español (España)',
  'fr-FR': 'Français',
  'it-IT': 'Italiano',
  'nb-NO': 'Norsk bokmål',
  'nl-NL': 'Nederlands',
  'pl-PL': 'Polski',
  'pt-BR': 'Português',
  'sv-SE': 'Svenska',
  'vi-VN': 'Tiếng Việt',
  'tr-TR': 'Türkçe',
  'hi-IN': 'हिन्दी',
  'th-TH': 'ไทย',
  'ja-JP': '日本語',
  'zh-Hans': '简体中文',
  'zh-Hant': '繁體中文',
  'ko-KR': '한국어',
};

let current: Locale = FALLBACK_LOCALE;

/** The language the interface is speaking right now. */
export function locale(): Locale {
  return current;
}

/** The BCP-47 tag for the current language — which is the language itself. */
export function localeTag(): Locale {
  return current;
}

/**
 * Speak `next` from here on, and stamp it on the document.
 *
 * The stamp is load-bearing beyond accessibility: `src/styles/scripts.css` keys
 * its per-script font stacks and line-breaking rules off `<html lang>`, so a
 * language whose tag never lands is a language rendered in the wrong font.
 *
 * Nothing repaints itself: the caller owns the redraw, because the app knows
 * what is on screen and this module does not.
 */
export function setLocale(next: Locale): void {
  current = next;
  document.documentElement.lang = next;
}

/** Whether a stored or guessed string names a language this build speaks. */
export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * A plain language code: two or three letters, no region.
 *
 * The region is deliberately dropped. "Which languages should the game be
 * translated into next" is answered by `es`; `es-MX` additionally narrows
 * where a player is, which is not being asked and so is not collected.
 */
const LANG_RE = /^[a-z]{2,3}$/;

/**
 * This browser's language preferences, best first.
 *
 * The one place either reading below touches the browser, so both are pure
 * functions of what they are handed. `navigator.language` is the fallback for
 * the browsers that never grew the plural.
 */
function browserPrefs(): readonly string[] {
  return navigator.languages?.length ? navigator.languages : [navigator.language ?? ''];
}

/**
 * The language this browser asks sites for first, or `undefined`.
 *
 * The head of the same preference list a server would receive as
 * `Accept-Language` — the header itself is unreadable from a page, and this
 * game is static files with no server to read it anyway. Refused rather than
 * truncated, the way a campaign tag is: a code that does not fit the shape is
 * indistinguishable from no code, and a truncated one would be a lie.
 */
export function preferredLanguage(prefs: readonly string[] = browserPrefs()): string | undefined {
  const primary = (prefs[0] ?? '').toLowerCase().split('-')[0];
  return LANG_RE.test(primary) ? primary : undefined;
}

/** A tag pulled apart far enough to match on. */
interface Subtags {
  language: string;
  script?: string;
  region?: string;
}

/**
 * The script a region implies, for a language written in more than one.
 *
 * Browsers still send `zh-TW` and `zh-CN` far more often than `zh-Hant` and
 * `zh-Hans`, and for Chinese the script is not a nicety: a reader of one
 * cannot comfortably read the other, so guessing wrong is worse than falling
 * back to a language they also listed.
 */
const SCRIPT_BY_REGION: Record<string, Record<string, string>> = {
  zh: { TW: 'Hant', HK: 'Hant', MO: 'Hant', CN: 'Hans', SG: 'Hans', MY: 'Hans' },
};

/** Split a tag into the three subtags matching cares about. */
function parseTag(tag: string): Subtags {
  const parts = tag.split('-').filter(Boolean);
  const out: Subtags = { language: (parts[0] ?? '').toLowerCase() };
  for (const part of parts.slice(1)) {
    // A script is four letters (Hant); a region is two letters (TW) or three
    // digits (419). Anything past those is a variant, which nothing matches on.
    if (/^[a-z]{4}$/i.test(part)) {
      out.script = part[0].toUpperCase() + part.slice(1).toLowerCase();
    } else if (/^([a-z]{2}|\d{3})$/i.test(part)) {
      out.region = part.toUpperCase();
    }
  }
  return out;
}

/** The script a tag names, or the one its region implies. */
function scriptOf(t: Subtags): string | undefined {
  return t.script ?? (t.region ? SCRIPT_BY_REGION[t.language]?.[t.region] : undefined);
}

/**
 * UN M49 region 419, Latin America and the Caribbean, as the countries in it.
 *
 * `es-419` is a macro-region rather than a country, so nobody's phone is ever
 * set to it: they are set to es-MX, es-AR, es-CO. Without this the game would
 * have to match those on language alone, and once two Spanishes are on offer
 * "the first Spanish in the list" is not an answer, it is a coin toss.
 */
const MACRO_REGION: Record<string, ReadonlySet<string>> = {
  '419': new Set([
    'AR',
    'BO',
    'BR',
    'BZ',
    'CL',
    'CO',
    'CR',
    'CU',
    'DO',
    'EC',
    'GT',
    'HN',
    'HT',
    'MX',
    'NI',
    'PA',
    'PE',
    'PR',
    'PY',
    'SV',
    'UY',
    'VE',
  ]),
};

/**
 * Which locale a language settles on when the browser names no region, or
 * names one no offered locale covers.
 *
 * Only needed for a language the game speaks more than one of. Spanish
 * resolves to Latin American: most Spanish speakers are there, and a bare
 * `es` or an unlisted region is likelier to be one of them than to be Spain,
 * which says so exactly (`es-ES`) when it means it.
 *
 * Chinese is the same shape one level up: the two entries differ by script
 * rather than by region, so a bare `zh` picks neither on its own. Simplified
 * answers it for the same reason — it is the script most Chinese readers
 * read, and a browser that means Traditional has `zh-Hant`, `zh-TW` and
 * `zh-HK` to say so with.
 */
const DEFAULT_FOR_LANGUAGE: Record<string, Locale> = {
  es: 'es-419',
  zh: 'zh-Hans',
};

/** Whether an offered locale's region covers the region being asked for. */
function covers(offered: string | undefined, want: string | undefined): boolean {
  if (!offered || !want) return false;
  return offered === want || (MACRO_REGION[offered]?.has(want) ?? false);
}

/**
 * The language to open in when the player has never chosen one.
 *
 * Best fit down the browser's own preference list: an exact tag wins, then a
 * shared language and script, then the language alone. `pt-PT` therefore
 * lands on `pt-BR` and `es-MX` on `es-419`, because another region of a
 * language you read beats a language you do not.
 *
 * Script is the one thing never traded away. A `zh-Hans` reader is not handed
 * `zh-Hant`; they fall through to the next language they listed, and to
 * English after that. A wrong script is not a near miss, it is unreadable.
 */
export function detectLocale(prefs: readonly string[] = browserPrefs()): Locale {
  const offered = LOCALES.map((l) => ({ locale: l, tags: parseTag(l) }));

  for (const raw of prefs) {
    const want = parseTag(raw);
    if (!want.language) continue;

    const exact = offered.find((o) => o.locale.toLowerCase() === raw.toLowerCase());
    if (exact) return exact.locale;

    // Same language, and a script that is either agreed on or not in question.
    const wantScript = scriptOf(want);
    const sameTongue = offered.filter(
      (o) => o.tags.language === want.language && (!wantScript || scriptOf(o.tags) === wantScript),
    );
    if (!sameTongue.length) continue;

    // The region decides between them: es-MX is inside es-419's macro-region,
    // es-ES is Spain and says so. Where the region settles nothing — a bare
    // `es`, or a country no offered locale covers — the language's own
    // default answers rather than whichever entry happened to sort first.
    const byRegion = sameTongue.find((o) => covers(o.tags.region, want.region));
    if (byRegion) return byRegion.locale;

    const fallback = DEFAULT_FOR_LANGUAGE[want.language];
    if (fallback && sameTongue.some((o) => o.locale === fallback)) return fallback;

    return sameTongue[0].locale;
  }
  return FALLBACK_LOCALE;
}
